<template>
    <MPDropdown
        v-if="hasMenu"
        :open="menuOpen"
        @update:open="(open: boolean) => (menuOpen = open)"
    >
        <MPDropdownTrigger />
        <MPDropdownContent>
            <MPDropdownItem
                v-if="canDownload"
                label="Download"
                @select="emit('download')"
            />
            <slot
                name="menu-items"
                :run="runFromMenu"
            />
            <MPDropdownItem
                v-if="canEdit"
                label="Edit"
                @select="runFromMenu($event, () => emit('edit'))"
            />
            <MPDropdownItem
                v-if="canDelete"
                label="Delete"
                @select="runFromMenu($event, () => emit('delete'))"
            />
        </MPDropdownContent>
    </MPDropdown>
</template>

<script setup lang="ts">
import MPDropdown from '@components/dropdown/MPDropdown.vue';
import MPDropdownContent from '@components/dropdown/MPDropdownContent.vue';
import MPDropdownItem from '@components/dropdown/MPDropdownItem.vue';
import MPDropdownTrigger from '@components/dropdown/MPDropdownTrigger.vue';

/**
 * A stored file's action menu, shared by its list row and its card. Renders
 * nothing when no action is allowed.
 *
 * MPDropdown rather than the tippy-based ThreeDotOption: it is reka-ui, the same
 * primitive MPModal is, so its portal positions correctly when the list is
 * inside a dialog. Tippy's popover lands off-screen there.
 */

const props = withDefaults(defineProps<{ canDownload?: boolean; canEdit?: boolean; canDelete?: boolean }>(), {
    canDownload: true,
    canEdit: false,
    canDelete: false,
});

const emit = defineEmits<{ download: []; edit: []; delete: [] }>();

const slots = useSlots();

const menuOpen = ref(false);

const hasMenu = computed(() => props.canDownload || props.canEdit || props.canDelete || !!slots['menu-items']);

/**
 * Runs a menu action that opens a dialog. `preventDefault` keeps the menu from
 * closing on select - without it its dismissal lands as a pointer event outside
 * the dialog that has just opened, and reka-ui closes that dialog again the
 * instant it appears - so the menu is closed by hand instead.
 *
 * Exposed to the `menu-items` slot so a caller's own dialog-opening item can use
 * it too.
 */
const runFromMenu = (event: Event, run: () => void) => {
    event.preventDefault();
    menuOpen.value = false;
    run();
};
</script>
